import React, { useState, useEffect, useCallback, useRef } from 'react';
import './App.css';
import logo from './assets/logo.png';

// Подключаем Firebase
import { auth, signInWithGoogle, logout, db } from './firebase';
import { onAuthStateChanged } from 'firebase/auth';
import { 
  doc, 
  setDoc, 
  onSnapshot, 
  updateDoc, 
  arrayUnion, 
  getDocs, 
  collection, 
  addDoc, 
  deleteDoc, 
  query, 
  orderBy 
} from 'firebase/firestore';

// КОНСТАНТЫ
const IMGBB_API_KEY = 'd7cc1ee0c935627a130894fe2a3ff863';
const TITLE_TEMPLATE = "Drawing All Phases in Incredibox Sprunki OC of  / From Phase 1 to 3";
const PREFIX_TEXT = "Drawing All Phases in Incredibox Sprunki OC of ";

function App() {
  // --- 1. ГЛОБАЛЬНЫЕ СОСТОЯНИЯ ---
  const [user, setUser] = useState(null);

  // АВТОРИЗАЦИЯ
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      setUser(currentUser);
    });
    return () => unsubscribe();
  }, []);

  // --- 2. НАВИГАЦИЯ И ВКЛАДКИ ---
  const [currentTab, setCurrentTab] = useState('calendar'); 
  const [selectedDate, setSelectedDate] = useState(null);
  
  // --- 3. НОВАЯ НИША (STATE & LOGIC) ---
  const [nicheItems, setNicheItems] = useState([]);
  const [editingId, setEditingId] = useState(null);

  // Загрузка данных "Новой ниши" (Realtime)
  useEffect(() => {
    if (!user) return;
    const q = query(collection(db, 'niche_items'), orderBy('timestamp', 'desc'));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const items = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setNicheItems(items);
    });
    return () => unsubscribe();
  }, [user]);

  const updateNicheDescription = async (id, text) => {
    try {
        const itemRef = doc(db, 'niche_items', id);
        await updateDoc(itemRef, { description: text });
    } catch (e) {
        console.error("Ошибка обновления текста:", e);
    }
  };

  const deleteNicheItem = async (e, id) => {
    e.stopPropagation();
    if (window.confirm('Удалить из ниши?')) {
      await deleteDoc(doc(db, 'niche_items', id));
    }
  };

  const renderTextWithLinks = (text) => {
    if (!text) return null;
    const urlRegex = /(https?:\/\/[^\s]+)/g;
    return text.split(urlRegex).map((part, index) => {
        if (part.match(urlRegex)) {
            return (
                <a 
                    key={index} 
                    href={part} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    onClick={(e) => e.stopPropagation()} 
                    className="niche-link"
                >
                    {part}
                </a>
            );
        }
        return part;
    });
  };

  // --- 4. КАЛЕНДАРЬ И ДАННЫЕ ДНЯ ---
  const [datesList, setDatesList] = useState([]);
  const [showArchive, setShowArchive] = useState(false);
  const [calendarScores, setCalendarScores] = useState({});

  useEffect(() => {
    const startDate = new Date('2026-01-03T00:00:00');
    const now = new Date();
    const kyivString = now.toLocaleString("en-US", { timeZone: "Europe/Kiev" });
    const kyivDate = new Date(kyivString);
    kyivDate.setHours(23, 59, 59, 999);

    const tempDates = [];
    let loopDate = new Date(startDate);

    while (loopDate <= kyivDate) {
      const day = loopDate.getDate().toString().padStart(2, '0');
      const month = (loopDate.getMonth() + 1).toString().padStart(2, '0');
      const year = loopDate.getFullYear();
      tempDates.push(`${day}.${month}.${year}`);
      loopDate.setDate(loopDate.getDate() + 1);
    }
    setDatesList(tempDates); 
  }, []);

  useEffect(() => {
    if (!user) return;
    const fetchScores = async () => {
        const querySnapshot = await getDocs(collection(db, "days"));
        const scores = {};
        querySnapshot.forEach((doc) => {
            const data = doc.data();
            scores[doc.id] = data.reportData?.score || 0;
        });
        setCalendarScores(scores);
    };
    fetchScores();
  }, [user, selectedDate, currentTab]); 

  const getScoreClass = (date) => {
      const score = calendarScores[date];
      if (score === undefined) return ''; 
      if (score >= 80) return 'good';     
      if (score >= 40) return 'okay';     
      return 'bad';                       
  };

  // --- 5. РАБОЧИЕ ДАННЫЕ ДНЯ (JOURNAL) ---
  const [activeStep, setActiveStep] = useState(0);
  const steps = ['Идея', 'Скетч', 'Название', 'Теги', 'Отчет'];

  const [ideaImages, setIdeaImages] = useState([]); 
  const [sketchImage, setSketchImage] = useState(null);
  const [videoTitle, setVideoTitle] = useState(TITLE_TEMPLATE);
  const [tags, setTags] = useState([]);

  const [reportData, setReportData] = useState({
    published: false,
    publishTime: '18:00',
    charCount: 2,
    score: 0
  });
  const [weeklyKPI, setWeeklyKPI] = useState(0);

  const [rawTags, setRawTags] = useState(''); 
  const [tagInput, setTagInput] = useState('');
  const [isCopied, setIsCopied] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  
  const [showTimePicker, setShowTimePicker] = useState(false);

  const titleInputRef = useRef(null);
  const [isFirstTitleClick, setIsFirstTitleClick] = useState(true);
  const fileInputRef = useRef(null);
  const mobileIdeaInputRef = useRef(null); 
  const rawTagsRef = useRef(null);

  // --- 6. РЕДАКТОР СКЕТЧЕЙ (CANVAS) ---
  const [showEditor, setShowEditor] = useState(false);
  const [slots, setSlots] = useState([
    { image: null, x: 0, y: 0, scale: 1 },
    { image: null, x: 0, y: 0, scale: 1 },
    { image: null, x: 0, y: 0, scale: 1 }
  ]);
  const [activeSlotIndex, setActiveSlotIndex] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [isResizing, setIsResizing] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [activeHandle, setActiveHandle] = useState(null);
  const [contextMenu, setContextMenu] = useState({ visible: false, x: 0, y: 0, slotIndex: null });

  // --- ЭФФЕКТЫ ---
  useEffect(() => { setRawTags(tags.join(',')); }, [tags]);
  
  useEffect(() => {
    if (rawTagsRef.current) {
      rawTagsRef.current.style.height = 'auto';
      rawTagsRef.current.style.height = rawTagsRef.current.scrollHeight + 'px';
    }
  }, [rawTags, activeStep]);

  useEffect(() => {
    if (!selectedDate || !user) return;
    setIsFirstTitleClick(true);
    const docRef = doc(db, 'days', selectedDate);
    const unsubscribe = onSnapshot(docRef, (docSnap) => {
      if (docSnap.exists()) {
        const data = docSnap.data();
        let loadedImages = data.ideaImages || [];
        if (loadedImages.length > 0 && typeof loadedImages[0] === 'string') {
             loadedImages = loadedImages.map(url => ({ url, isHot: false, id: url }));
        }
        setIdeaImages(loadedImages);
        setSketchImage(data.sketchImage || null);
        setVideoTitle(data.videoTitle || TITLE_TEMPLATE);
        setTags(data.tags || []);
        setReportData(data.reportData || { published: false, publishTime: '18:00', charCount: 2, score: 0 });
      } else {
        setIdeaImages([]);
        setSketchImage(null);
        setVideoTitle(TITLE_TEMPLATE);
        setTags([]);
        setReportData({ published: false, publishTime: '18:00', charCount: 2, score: 0 });
      }
    });
    setActiveStep(0);
    setTagInput('');
    setSelectedImage(null);
    return () => unsubscribe();
  }, [selectedDate, user]);

  useEffect(() => {
    if (activeStep === 4 && user) {
        fetchWeeklyKPI();
    }
  }, [activeStep, user]);

  const fetchWeeklyKPI = async () => {
    const daysCollection = collection(db, 'days');
    const querySnapshot = await getDocs(daysCollection);
    let totalScore = 0;
    let count = 0;
    querySnapshot.forEach((doc) => {
        const data = doc.data();
        if (data.reportData && data.reportData.score !== undefined) {
            totalScore += data.reportData.score;
            count++;
        }
    });
    setWeeklyKPI(count > 0 ? Math.round(totalScore / count) : 0);
  };

  const saveDataToFirebase = async (field, value) => {
    if (!selectedDate || !user) return;
    setIsSaving(true);
    try {
      const docRef = doc(db, 'days', selectedDate);
      await setDoc(docRef, { [field]: value }, { merge: true });
      setTimeout(() => setIsSaving(false), 1500);
    } catch (e) {
      console.error("Ошибка сохранения:", e);
      setIsSaving(false);
    }
  };

  const handleTitleClick = () => {
    if (isFirstTitleClick && titleInputRef.current) {
      const cursorPosition = PREFIX_TEXT.length;
      titleInputRef.current.setSelectionRange(cursorPosition, cursorPosition);
      setIsFirstTitleClick(false);
    }
  };

  const toBase64 = file => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result.toString().replace(/^data:image\/\w+;base64,/, ""));
    reader.onerror = error => reject(error);
  });

  const uploadToImgBB = async (file) => {
    try {
      const base64Str = await toBase64(file);
      const formData = new FormData();
      formData.append('image', base64Str);
      const response = await fetch(`https://api.imgbb.com/1/upload?key=${IMGBB_API_KEY}`, {
        method: 'POST', body: formData,
      });
      const data = await response.json();
      if (data.success) return data.data.url;
      else throw new Error(data.error ? data.error.message : 'Ошибка API');
    } catch (err) {
      console.error("Upload Error:", err);
      throw err;
    }
  };

  const handleUploadLogic = async (files) => {
    setUploading(true);
    try {
      const urls = await Promise.all(Array.from(files).map(file => uploadToImgBB(file)));
      const newImageObjects = urls.map(url => ({
          url: url,
          isHot: false,
          id: Date.now() + Math.random().toString() 
      }));
      
      const docRef = doc(db, 'days', selectedDate);
      await setDoc(docRef, { ideaImages: arrayUnion(...newImageObjects) }, { merge: true });
      
    } catch (error) { 
        alert("Ошибка загрузки: " + error.message); 
    } finally { 
        setUploading(false); 
    }
  };

  const handlePaste = useCallback(async (e) => {
    // 1. Приоритет: Редактор Скетчей
    if (showEditor) {
      const items = e.clipboardData.items;
      for (let i = 0; i < items.length; i++) {
        if (items[i].type.indexOf('image') !== -1) {
            const file = items[i].getAsFile();
            const reader = new FileReader();
            reader.onload = (event) => {
                const img = new Image();
                img.onload = () => {
                    setSlots(prev => {
                        const newSlots = [...prev];
                        newSlots[activeSlotIndex] = { image: img, x: 0, y: 0, scale: 1 };
                        return newSlots;
                    });
                };
                img.src = event.target.result;
            };
            reader.readAsDataURL(file);
            e.preventDefault();
            break;
        }
      }
      return;
    }

    // 2. Приоритет: Вкладка "Новая ниша"
    if (currentTab === 'niche') {
        const items = e.clipboardData.items;
        const files = [];
        for (let i = 0; i < items.length; i++) {
            if (items[i].type.indexOf('image') !== -1) {
                files.push(items[i].getAsFile());
            }
        }
        if (files.length > 0) {
            e.preventDefault();
            setUploading(true);
            try {
                const urls = await Promise.all(Array.from(files).map(file => uploadToImgBB(file)));
                await Promise.all(urls.map(url => 
                    addDoc(collection(db, 'niche_items'), {
                        url: url,
                        description: '', 
                        timestamp: Date.now()
                    })
                ));
            } catch (error) {
                alert("Ошибка загрузки в нишу: " + error.message);
            } finally {
                setUploading(false);
            }
        }
        return;
    }

    // 3. Приоритет: Daily Journal (вкладка Идеи)
    if (!selectedDate || activeStep !== 0 || selectedImage || !user) return;
    const items = e.clipboardData.items;
    const files = [];
    for (let i = 0; i < items.length; i++) {
      if (items[i].type.indexOf('image') !== -1) {
        files.push(items[i].getAsFile());
      }
    }
    if (files.length > 0) {
        e.preventDefault();
        handleUploadLogic(files);
    }
  }, [activeStep, selectedImage, selectedDate, user, showEditor, activeSlotIndex, currentTab]);

  useEffect(() => {
    window.addEventListener('paste', handlePaste);
    return () => window.removeEventListener('paste', handlePaste);
  }, [handlePaste]);

  useEffect(() => {
    const handleClickOutside = () => setContextMenu({ ...contextMenu, visible: false });
    window.addEventListener('click', handleClickOutside);
    return () => window.removeEventListener('click', handleClickOutside);
  }, [contextMenu]);

  const handleMouseDownImage = (e, index) => {
    if (!slots[index].image) return;
    if (e.button === 2) return;
    e.preventDefault(); e.stopPropagation();
    setActiveSlotIndex(index);
    setIsDragging(true); setIsResizing(false);
    setDragStart({ x: e.clientX, y: e.clientY });
  };

  const handleMouseDownHandle = (e, index, handleType) => {
    e.preventDefault(); e.stopPropagation();
    setActiveSlotIndex(index); setActiveHandle(handleType);
    setIsResizing(true); setIsDragging(false);
    setDragStart({ x: e.clientX, y: e.clientY });
  };

  const handleMouseMove = (e) => {
    if (isDragging && activeSlotIndex !== null) {
        e.preventDefault();
        const dx = e.clientX - dragStart.x;
        const dy = e.clientY - dragStart.y;
        setDragStart({ x: e.clientX, y: e.clientY });
        setSlots(prev => {
            const newSlots = [...prev];
            const slot = newSlots[activeSlotIndex];
            slot.x += dx; slot.y += dy;
            return newSlots;
        });
    }
    if (isResizing && activeSlotIndex !== null) {
        e.preventDefault();
        const dy = e.clientY - dragStart.y; 
        let directionMultiplier = 1;
        if (activeHandle === 'tl' || activeHandle === 'tr') directionMultiplier = -1;
        const scaleChange = dy * 0.005 * directionMultiplier;
        setSlots(prev => {
            const newSlots = [...prev];
            const slot = newSlots[activeSlotIndex];
            slot.scale = Math.max(0.1, slot.scale + scaleChange);
            return newSlots;
        });
        setDragStart({ x: e.clientX, y: e.clientY });
    }
  };

  const handleMouseUp = () => { setIsDragging(false); setIsResizing(false); setActiveHandle(null); };

  const handleWheel = (e, index) => {
    if (!slots[index].image) return;
    e.stopPropagation();
    const scaleAmount = -e.deltaY * 0.001;
    setSlots(prev => {
      const newSlots = [...prev];
      newSlots[index].scale = Math.max(0.1, newSlots[index].scale + scaleAmount);
      return newSlots;
    });
  };

  const handleContextMenu = (e, index) => {
    e.preventDefault();
    if (!slots[index].image) return;
    setContextMenu({ visible: true, x: e.clientX, y: e.clientY, slotIndex: index });
  };

  const handleDeleteSlotImage = () => {
    if (contextMenu.slotIndex !== null) {
        setSlots(prev => {
            const newSlots = [...prev];
            newSlots[contextMenu.slotIndex] = { image: null, x: 0, y: 0, scale: 1 };
            return newSlots;
        });
    }
    setContextMenu({ ...contextMenu, visible: false });
  };

  const exportImage = () => {
    const canvas = document.createElement('canvas');
    canvas.width = 1920; canvas.height = 1080;
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = '#FFFFFF'; ctx.fillRect(0, 0, 1920, 1080);
    const colWidth = 1920 / 3;
    const domSlot = document.querySelector('.editor-slot'); 
    let domWidth = 1, domHeight = 1;
    if (domSlot) { domWidth = domSlot.clientWidth; domHeight = domSlot.clientHeight; }
    const ratioX = colWidth / domWidth;
    const colHeight = 1080;

    slots.forEach((slot, i) => {
      if (!slot.image) return;
      ctx.save();
      ctx.beginPath(); ctx.rect(i * colWidth, 0, colWidth, 1080); ctx.clip();
      const imgRatio = slot.image.width / slot.image.height;
      const slotRatio = domWidth / domHeight;
      let baseScale;
      if (imgRatio > slotRatio) baseScale = colWidth / slot.image.width;
      else baseScale = colHeight / slot.image.height;
      const centerX = i * colWidth + colWidth / 2;
      const centerY = colHeight / 2;
      ctx.translate(centerX + (slot.x * ratioX), centerY + (slot.y * ratioX));
      const finalScale = baseScale * slot.scale;
      ctx.scale(finalScale, finalScale);
      ctx.drawImage(slot.image, -slot.image.width / 2, -slot.image.height / 2);
      ctx.restore();
    });

    ctx.fillStyle = '#000000';
    ctx.fillRect(colWidth - 3, 0, 6, 1080); ctx.fillRect(colWidth * 2 - 3, 0, 6, 1080);
    ctx.strokeStyle = '#000000'; ctx.lineWidth = 10; ctx.strokeRect(0, 0, 1920, 1080);
    const link = document.createElement('a');
    link.download = `Sketch_${selectedDate || 'new'}.jpg`;
    link.href = canvas.toDataURL('image/jpeg', 0.95);
    link.click();
  };

  const handleDragOver = (e) => e.preventDefault();
  
  const handleDrop = async (e) => {
    e.preventDefault();
    if (!selectedDate || activeStep !== 0 || !user) return;
    const files = Array.from(e.dataTransfer.files).filter(file => file.type.startsWith('image/'));
    if (files.length > 0) handleUploadLogic(files);
  };

  const handleMobileIdeaUpload = async (e) => {
    const files = e.target.files;
    if (files && files.length > 0) {
        handleUploadLogic(files);
        if (mobileIdeaInputRef.current) mobileIdeaInputRef.current.value = '';
    }
  };

  const deleteIdeaImage = async (e, imageObj) => {
    e.stopPropagation();
    if (!window.confirm("Удалить?")) return;
    const newImages = ideaImages.filter(img => img.id !== imageObj.id);
    await saveDataToFirebase('ideaImages', newImages);
  };

  const toggleHot = async (e, imageObj) => {
      e.stopPropagation();
      const newImages = ideaImages.map(img => {
          if (img.id === imageObj.id) {
              return { ...img, isHot: !img.isHot };
          }
          return img;
      });
      await saveDataToFirebase('ideaImages', newImages);
  };

  const handleSketchUpload = async (e) => {
    const file = e.target.files[0];
    if (file) {
      setUploading(true);
      try {
        const url = await uploadToImgBB(file);
        await setDoc(doc(db, 'days', selectedDate), { sketchImage: url }, { merge: true });
      } catch (error) { alert("Error: " + error.message); }
      finally { setUploading(false); }
    }
  };

  const deleteSketch = async (e) => {
    e.stopPropagation();
    if (!window.confirm("Удалить скетч?")) return;
    await updateDoc(doc(db, 'days', selectedDate), { sketchImage: null });
  };

  const downloadSketch = (e) => { e.stopPropagation(); window.open(sketchImage, '_blank'); };

  const isStepComplete = (index) => {
    if (index === 0) return ideaImages.length > 0;
    if (index === 1) return sketchImage !== null;
    if (index === 2) return videoTitle.trim().length > 0 && videoTitle !== TITLE_TEMPLATE;
    if (index === 3) return tags.length > 0;
    return false;
  };
  
  const isTitleOverLimit = videoTitle.length > 100;
  const totalTagsChars = tags.join(',').length;
  const isTagsOverLimit = totalTagsChars > 500;
  
  const handleTagKeyDown = async (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      const newTags = tagInput.split(',').map(t => t.trim()).filter(t => t.length > 0);
      if (newTags.length > 0) {
        const updatedTags = [...tags, ...newTags];
        setTags(updatedTags); setTagInput('');
        await saveDataToFirebase('tags', updatedTags);
      }
    }
  };
  
  const removeTag = async (index) => {
    const updatedTags = tags.filter((_, i) => i !== index);
    setTags(updatedTags);
    await saveDataToFirebase('tags', updatedTags);
  };
  
  const handleRawChange = (e) => setRawTags(e.target.value);
  
  const handleRawBlur = async () => {
    const newTags = rawTags.split(',').map(t => t.trim()).filter(t => t.length > 0);
    setTags(newTags);
    await saveDataToFirebase('tags', newTags);
  };
  
  const handleCopyTags = () => {
    navigator.clipboard.writeText(tags.join(','));
    setIsCopied(true); setTimeout(() => setIsCopied(false), 2000);
  };

  const calculateDailyScore = (currentPublished, currentTime, chars) => {
    let score = 0;
    if (ideaImages.length > 0) score += 2;
    if (sketchImage) score += 2;
    if (videoTitle.length > 0 && videoTitle !== TITLE_TEMPLATE) score += 2;
    if (tags.length > 0) score += 2;

    if (chars === 3) score += 15;
    else if (chars === 2) score += 5;

    if (currentPublished) {
        score += 50;
        const hour = parseInt(currentTime.split(':')[0]);
        if (hour >= 17 && hour <= 19) score += 27; 
        else if (hour >= 12 && hour <= 22) score += 10;
        else score += 0;
    } else {
        score -= 50;
    }
    return score;
  };

  const handleReportSave = async () => {
    const score = calculateDailyScore(reportData.published, reportData.publishTime, reportData.charCount);
    const newData = { ...reportData, score: score };
    setReportData(newData);
    await saveDataToFirebase('reportData', newData);
    fetchWeeklyKPI();
  };

  const hours = Array.from({length: 24}, (_, i) => i.toString().padStart(2, '0'));
  const minutes = Array.from({length: 12}, (_, i) => (i * 5).toString().padStart(2, '0'));

  const updateTime = (type, val) => {
    const [h, m] = reportData.publishTime.split(':');
    let newTime = reportData.publishTime;
    if (type === 'hour') newTime = `${val}:${m}`;
    if (type === 'minute') newTime = `${h}:${val}`;
    setReportData({...reportData, publishTime: newTime});
  };

  const sortedIdeaImages = [...ideaImages].sort((a, b) => {
      if (a.isHot && !b.isHot) return -1;
      if (!a.isHot && b.isHot) return 1;
      return 0;
  });

  if (!user) return (
    <div className="login-screen">
      <div className="login-card">
        <img src={logo} className="home-logo" alt="logo" />
        <h1 className="app-name-login">Amore Archive</h1>
        <p className="login-text">Приватное хранилище креатива</p>
        <button className="login-btn" onClick={signInWithGoogle}>Войти через Google</button>
      </div>
    </div>
  );

  return (
    <div className="App">
      <header className="global-header">
         <div className="header-left" onClick={() => { setCurrentTab('calendar'); setSelectedDate(null); }}>
             <img src={logo} alt="Logo" className="header-logo-img" />
         </div>
         <nav className="header-nav">
             <button 
                className={`nav-btn ${currentTab === 'calendar' ? 'active-nav' : ''}`} 
                onClick={() => { setCurrentTab('calendar'); setSelectedDate(null); }}
             >
                Календарь
             </button>
             <button className="nav-btn" onClick={() => setShowEditor(true)}>Скетч</button>
             <button 
                className={`nav-btn ${currentTab === 'niche' ? 'active-nav' : ''}`} 
                onClick={() => { setCurrentTab('niche'); setSelectedDate(null); }}
             >
                Новая ниша
             </button>
             <button className="nav-btn logout" onClick={logout}>Выход</button>
         </nav>
      </header>

      {/* --- ГЛОБАЛЬНЫЕ КОМПОНЕНТЫ (МОДАЛКИ) --- */}

      {contextMenu.visible && (
        <div className="context-menu" style={{ top: contextMenu.y, left: contextMenu.x }} onClick={(e) => e.stopPropagation()}>
            <button onClick={handleDeleteSlotImage} className="context-menu-item delete">Удалить изображение</button>
        </div>
      )}

      {/* MODAL: TIME PICKER */}
      {showTimePicker && (
          <div className="time-picker-overlay" onClick={() => setShowTimePicker(false)}>
              <div className="time-picker-modal" onClick={(e) => e.stopPropagation()}>
                  <div className="time-picker-header">
                      <span>Выберите время</span>
                      <button className="close-time-btn" onClick={() => setShowTimePicker(false)}>×</button>
                  </div>
                  <div className="time-columns">
                      <div className="time-col">
                          {hours.map(h => (
                              <div 
                                key={h} 
                                className={`time-cell ${reportData.publishTime.startsWith(h) ? 'active' : ''}`}
                                onClick={() => updateTime('hour', h)}
                              >
                                {h}
                              </div>
                          ))}
                      </div>
                      <div className="time-col">
                          {minutes.map(m => (
                              <div 
                                key={m} 
                                className={`time-cell ${reportData.publishTime.endsWith(m) ? 'active' : ''}`}
                                onClick={() => updateTime('minute', m)}
                              >
                                {m}
                              </div>
                          ))}
                      </div>
                  </div>
              </div>
          </div>
      )}

      {/* MODAL: EDITOR */}
      {showEditor && (
        <div className="editor-overlay">
          <div className="editor-container">
            <div className="editor-header-ui">
              <h2>Скетч-редактор</h2>
              <div className="editor-controls">
                <button className="editor-btn close" onClick={() => setShowEditor(false)}>Закрыть</button>
                <button className="editor-btn save" onClick={exportImage}>Скачать (HD)</button>
              </div>
            </div>
            <div className="editor-workspace" onMouseMove={handleMouseMove} onMouseUp={handleMouseUp} onMouseLeave={handleMouseUp}>
              <div className="canvas-wrapper">
                {slots.map((slot, index) => (
                  <div key={index} className={`editor-slot ${activeSlotIndex === index ? 'active-slot' : ''}`} onClick={() => setActiveSlotIndex(index)} onContextMenu={(e) => handleContextMenu(e, index)}>
                    {slot.image && (
                      <div className="slot-img-content" style={{ backgroundImage: `url(${slot.image.src})`, transform: `translate(${slot.x}px, ${slot.y}px) scale(${slot.scale})` }} onMouseDown={(e) => handleMouseDownImage(e, index)} onWheel={(e) => handleWheel(e, index)}>
                         {activeSlotIndex === index && (
                             <div className="transform-box">
                                 <div className="handle tl" onMouseDown={(e) => handleMouseDownHandle(e, index, 'tl')}></div>
                                 <div className="handle tr" onMouseDown={(e) => handleMouseDownHandle(e, index, 'tr')}></div>
                                 <div className="handle bl" onMouseDown={(e) => handleMouseDownHandle(e, index, 'bl')}></div>
                                 <div className="handle br" onMouseDown={(e) => handleMouseDownHandle(e, index, 'br')}></div>
                             </div>
                         )}
                      </div>
                    )}
                    {!slot.image && activeSlotIndex === index && <span className="slot-hint">Активен. Жми Ctrl+V</span>}
                  </div>
                ))}
                <div className="slot-lines"><div className="line line-1"></div><div className="line line-2"></div></div>
              </div>
            </div>
            <p className="editor-tip">Выбери блок • Вставь (Cmd+V) • Правая кнопка - Удалить • Тяни за углы - Зум</p>
          </div>
        </div>
      )}

      {/* MODAL: FULLSCREEN IMAGE (ТЕПЕРЬ ГЛОБАЛЬНОЕ) */}
      {selectedImage && (
        <div className="fullscreen-modal" onClick={() => setSelectedImage(null)}>
          <img 
            src={selectedImage} 
            className="fullscreen-image" 
            alt="Full view" 
            onClick={(e) => setSelectedImage(null)} // Клик по картинке тоже закрывает
          />
        </div>
      )}

      {/* --- ЭКРАНЫ --- */}

      {currentTab === 'niche' && (
        <div className="niche-screen">
          <div className="niche-header">
            <h1>Референсы & Идеи</h1>
            <p className="niche-subtitle">{uploading ? "Загрузка..." : "Нажми Cmd+V (Ctrl+V) чтобы добавить скриншот"}</p>
          </div>
          
          <div className="niche-grid">
            {nicheItems.map((item) => (
              <div key={item.id} className="niche-card">
                <div className="niche-img-container" onClick={() => setSelectedImage(item.url)}>
                  <img src={item.url} alt="reference" />
                  <button className="niche-delete-btn" onClick={(e) => deleteNicheItem(e, item.id)}>×</button>
                </div>
                
                {/* --- ТЕКСТОВОЕ ПОЛЕ (ПРОСМОТР / РЕДАКТИРОВАНИЕ) --- */}
                <div 
                    className="niche-desc-container"
                    onClick={() => setEditingId(item.id)}
                >
                    {editingId === item.id ? (
                         <textarea 
                            className="niche-input" 
                            placeholder="Добавить заметку..." 
                            defaultValue={item.description}
                            autoFocus
                            rows={1}
                            onBlur={(e) => {
                                setEditingId(null); 
                                updateNicheDescription(item.id, e.target.value);
                            }}
                            onInput={(e) => {
                                e.target.style.height = 'auto';
                                e.target.style.height = e.target.scrollHeight + 'px';
                            }}
                            ref={(textarea) => {
                                if (textarea) {
                                    textarea.style.height = 'auto';
                                    textarea.style.height = textarea.scrollHeight + 'px';
                                }
                            }}
                        />
                    ) : (
                        <div className="niche-text-view">
                            {item.description ? renderTextWithLinks(item.description) : <span className="niche-placeholder">Добавить заметку...</span>}
                        </div>
                    )}
                </div>

              </div>
            ))}
          </div>
          
          {nicheItems.length === 0 && !uploading && (
             <div className="empty-niche-state">
                Ниша пуста. Вставь что-нибудь вдохновляющее.
             </div>
          )}
        </div>
      )}

      {currentTab === 'calendar' && !selectedDate && (
        <div className="date-selection-screen">
          <div className="calendar-grid">
            <div className={`date-card archive-card ${showArchive ? 'active' : ''}`} onClick={() => setShowArchive(!showArchive)}>
               <div className="archive-icon" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                 {showArchive ? (
                   <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="18 15 12 9 6 15"></polyline></svg>
                 ) : (
                   <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="21 8 21 21 3 21 3 8"></polyline><rect x="1" y="3" width="22" height="5"></rect><line x1="10" y1="12" x2="14" y2="12"></line></svg>
                 )}
               </div>
            </div>
            {(showArchive ? datesList : datesList.slice(-2)).map((date) => (
              <div key={date} className={`date-card ${getScoreClass(date)}`} onClick={() => setSelectedDate(date)}>
                <div className="hover-bg-fill"></div>
                <div className="date-content-wrapper">
                    <span className="date-text">{date.slice(0, 5)}</span>
                    <span className="year-text">{date.slice(6)}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {currentTab === 'calendar' && selectedDate && (
        <div className="workspace-wrapper">
          {/* Здесь был fullscreen-modal, теперь он глобальный (выше) */}
          
          <div className="main-content">
            <div className="workspace-status-bar">
                <div className="current-date-badge">{selectedDate}</div>
                <div className="status-right">
                  {isSaving && <span className="status-saved">Сохранено</span>}
                  {uploading && <span className="status-loading">Загрузка...</span>}
                </div>
            </div>

            <div className="stepper-container">
              {steps.map((label, index) => {
                const isActive = index <= activeStep;
                return (
                  <React.Fragment key={index}>
                    <div className={`step-item ${isActive ? 'active' : ''} ${isStepComplete(index) ? 'completed' : ''}`} onClick={() => setActiveStep(index)}>
                      <div className="step-circle">{isStepComplete(index) && <svg className="checkmark-icon" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3"><path d="M20 6L9 17L4 12"/></svg>}</div>
                      <span className="step-label">{label}</span>
                    </div>
                    {index < steps.length - 1 && <div className={`step-connector ${index < activeStep ? 'black' : ''}`}></div>}
                  </React.Fragment>
                );
              })}
            </div>

            <div className="page-content">
              {activeStep === 0 && (
                <div className="ideas-page" onDragOver={handleDragOver} onDrop={handleDrop}>
                  <div className="mobile-upload-container">
                    <input type="file" multiple accept="image/*" ref={mobileIdeaInputRef} style={{ display: 'none' }} onChange={handleMobileIdeaUpload} />
                    <button className="mobile-add-btn" onClick={() => mobileIdeaInputRef.current.click()}>+ Добавить фото</button>
                  </div>
                  {sortedIdeaImages.length === 0 ? (
                    <div className="paste-instruction"><p>{uploading ? "Загружаем..." : "Нажми Cmd+V или перетащи файлы"}</p></div>
                  ) : (
                    <div className="images-grid">
                      {sortedIdeaImages.map((imgObj, index) => (
                        <div key={imgObj.id || index} className="image-wrapper" onClick={() => setSelectedImage(imgObj.url)}>
                          <img src={imgObj.url} alt="idea" loading="lazy" />
                          {imgObj.isHot && (
                              <div className="permanent-hot-badge">
                                  <svg viewBox="0 0 24 24" fill="currentColor" width="22" height="22">
                                      <path d="M11.56 2.375c-2.422 2.657-4.225 6.007-4.225 8.783 0 2.21 1.258 4.148 3.104 5.105.358.186.438.67.16 1.055a3.91 3.91 0 0 1-2.076 1.34c-1.28.312-2.316-.273-2.316-.273s1.21 2.99 4.293 2.99c3.385 0 6-2.923 6-6.426 0-3.32-2.197-7.25-4.94-12.574z"/>
                                  </svg>
                              </div>
                          )}
                          <div className="top-right-actions">
                              <button 
                                  className={`mini-action-btn fire ${imgObj.isHot ? 'active ignite-anim' : ''}`} 
                                  onClick={(e) => toggleHot(e, imgObj)}
                                  title={imgObj.isHot ? "Остудить" : "В топ"}
                              >
                                  <svg viewBox="0 0 24 24" fill="currentColor" width="26" height="26">
                                      <path d="M11.56 2.375c-2.422 2.657-4.225 6.007-4.225 8.783 0 2.21 1.258 4.148 3.104 5.105.358.186.438.67.16 1.055a3.91 3.91 0 0 1-2.076 1.34c-1.28.312-2.316-.273-2.316-.273s1.21 2.99 4.293 2.99c3.385 0 6-2.923 6-6.426 0-3.32-2.197-7.25-4.94-12.574z"/>
                                  </svg>
                              </button>
                              <button className="mini-action-btn delete" onClick={(e) => deleteIdeaImage(e, imgObj)}>
                                  <svg xmlns="http://www.w3.org/2000/svg" width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                                      <line x1="18" y1="6" x2="6" y2="18"></line>
                                      <line x1="6" y1="6" x2="18" y2="18"></line>
                                  </svg>
                              </button>
                          </div>
                        </div>
                      ))}
                      {uploading && <div className="image-wrapper uploading-placeholder">
                          <svg className="animate-spin" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <circle cx="12" cy="12" r="10" opacity="0.25"></circle>
                              <path d="M4 12a8 8 0 0 1 8-8" opacity="0.75"></path>
                          </svg>
                      </div>}
                    </div>
                  )}
                </div>
              )}
              
              {activeStep === 1 && (
                <div className="sketch-page">
                  <div className="sketch-header-actions" style={{marginBottom: '20px', display: 'flex', justifyContent: 'center'}}>
                      <button className="create-sketch-btn" onClick={() => setShowEditor(true)}>✏️ Создать скетч</button>
                  </div>
                  <input type="file" ref={fileInputRef} style={{ display: 'none' }} accept="image/*" onChange={handleSketchUpload} />
                  {!sketchImage ? (
                    <div className="upload-box" onClick={() => fileInputRef.current.click()}>
                      <div className="upload-content"><span className="upload-text">{uploading ? "Загрузка..." : "Загрузить скетч"}</span></div>
                    </div>
                  ) : (
                    <div className="sketch-preview-container">
                      <div className="image-wrapper sketch-wrapper" onClick={() => setSelectedImage(sketchImage)}>
                        <img src={sketchImage} alt="Sketch" />
                        <div className="hover-overlay sketch-overlay">
                          <div className="sketch-actions">
                            <button className="icon-btn download-btn" onClick={downloadSketch}>↓</button>
                            <button className="icon-btn delete-sketch-btn-top" onClick={deleteSketch}>×</button>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}
              {activeStep === 2 && (
                <div className="title-page">
                  <div className="title-input-container">
                    <textarea ref={titleInputRef} className={`title-input ${isTitleOverLimit ? 'error' : ''}`} placeholder="Title..." value={videoTitle} onClick={handleTitleClick} onChange={(e) => setVideoTitle(e.target.value)} onBlur={() => saveDataToFirebase('videoTitle', videoTitle)} />
                    <div className="char-counter">{videoTitle.length}/100</div>
                  </div>
                </div>
              )}
              {activeStep === 3 && (
                <div className="tags-page">
                  <div className="tags-header">
                      <span className={`chars-count ${isTagsOverLimit ? 'limit-error' : ''}`}>{totalTagsChars}/500</span>
                      <button className="copy-tags-btn" onClick={handleCopyTags}>
                          {isCopied ? <span className="copy-success">Скопировано!</span> : <span>Копировать</span>}
                      </button>
                  </div>
                  <div className="tags-container">
                      {tags.map((tag, index) => (
                          <div key={index} className={`tag-chip ${isTagsOverLimit ? 'error' : ''}`}>
                              <span>{tag}</span>
                              <button onClick={() => removeTag(index)} className="tag-remove">×</button>
                          </div>
                      ))}
                      <input type="text" className="tag-input" placeholder="Введите тег и Enter..." value={tagInput} onChange={(e) => setTagInput(e.target.value)} onKeyDown={handleTagKeyDown} />
                  </div>
                  <div className="raw-tags-section">
                    <p className="raw-tags-label">Прямое редактирование (текст):</p>
                    <textarea ref={rawTagsRef} className="raw-tags-input" value={rawTags} onChange={handleRawChange} onBlur={handleRawBlur} placeholder="тег1, тег2, тег3..." />
                  </div>
                </div>
              )}

              {activeStep === 4 && (
                  <div className="report-page">
                      <div className="report-card">
                          <div className="report-section">
                              <p className="section-label">Опубликовал видео?</p>
                              <div className="toggle-group">
                                  <button 
                                      className={`toggle-btn ${!reportData.published ? 'active' : ''}`} 
                                      onClick={() => setReportData({...reportData, published: false})}
                                  >
                                      Нет
                                  </button>
                                  <button 
                                      className={`toggle-btn ${reportData.published ? 'active' : ''}`} 
                                      onClick={() => setReportData({...reportData, published: true})}
                                  >
                                      Да
                                  </button>
                              </div>
                          </div>
                          {reportData.published && (
                              <div className="animate-fade">
                                  <div className="report-section time-display">
                                      <p className="section-label" style={{marginBottom: '0'}}>Время публикации</p>
                                      <div className="time-picker-trigger" onClick={() => setShowTimePicker(true)}>
                                          {reportData.publishTime}
                                      </div>
                                  </div>
                                  <div className="report-section">
                                      <p className="section-label">Персонажи</p>
                                      <div className="toggle-group">
                                          <button 
                                              className={`toggle-btn ${reportData.charCount === 2 ? 'active' : ''}`} 
                                              onClick={() => setReportData({...reportData, charCount: 2})}
                                          >
                                              2
                                          </button>
                                          <button 
                                              className={`toggle-btn ${reportData.charCount === 3 ? 'active' : ''}`} 
                                              onClick={() => setReportData({...reportData, charCount: 3})}
                                          >
                                              3+
                                          </button>
                                      </div>
                                  </div>
                              </div>
                          )}
                          <div className="report-footer">
                              <div className="kpi-block">
                                  <span className="kpi-label">Day Score</span>
                                  <span className="kpi-value">
                                      {calculateDailyScore(reportData.published, reportData.publishTime, reportData.charCount).toString().padStart(3, '0')}
                                  </span>
                              </div>
                              <div className="kpi-block" style={{alignItems: 'flex-end'}}>
                                  <span className="kpi-label">Efficiency</span>
                                  <span className={`kpi-value ${weeklyKPI > 50 ? 'good' : 'bad'}`}>
                                      {weeklyKPI}%
                                  </span>
                              </div>
                          </div>
                      </div>
                      <button className="save-report-btn" onClick={handleReportSave}>
                          {isSaving ? 'Сохранено' : 'Подтвердить'}
                      </button>
                  </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default App;